import type { LucideIcon } from "lucide-react";
import { cx } from "./cx";

/**
 * Icon system — ONE library (Lucide, ISC), stroke 2 (the friendly-bold weight
 * matching Gabarito headlines), sizes from icon.* tokens, color inherits the
 * text role (DESIGN_SYSTEM.md §10).
 *
 * Call sites import icons from THIS module, never from "lucide-react"
 * directly — the single-library rule stays enforceable by grep.
 */

export type IconSize = "sm" | "md" | "lg" | "xl";

export interface IconProps {
  icon: LucideIcon;
  size?: IconSize;
  /** Accessible label; omit for purely decorative icons (default: hidden). */
  label?: string;
  className?: string;
}

export function Icon({ icon: Glyph, size = "md", label, className }: IconProps) {
  return (
    <Glyph
      className={cx("pz-icon", `pz-icon--${size}`, className)}
      strokeWidth={2}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      focusable={false}
    />
  );
}

/* The curated Plotzio set — extend here, never by importing lucide elsewhere. */
export {
  ArrowRight,
  Check,
  ChevronDown,
  CircleAlert,
  CircleCheck,
  ClipboardPaste,
  Clock,
  Code,
  Copy,
  Circle,
  Download,
  Ellipsis,
  ExternalLink,
  Eye,
  FileSpreadsheet,
  FileText,
  Globe,
  Hexagon,
  Image,
  Info,
  LandPlot,
  Layers,
  Link2,
  LocateFixed,
  Lock,
  Map,
  MapPin,
  MapPinned,
  Maximize,
  Menu,
  Minus,
  MessageSquare,
  Navigation,
  Diamond,
  Pencil,
  Plus,
  RotateCcw,
  Route,
  Save,
  Search,
  Settings,
  Shapes,
  Share2,
  SlidersHorizontal,
  Sparkles,
  Spline,
  Square,
  Star,
  Table2,
  Trash2,
  Triangle,
  TriangleAlert,
  Upload,
  User,
  X,
} from "lucide-react";
export type { LucideIcon } from "lucide-react";
